/**
 * AI Translator
 * Handles AI-powered translation of form content
 */

import { API } from './api.js';

export class AITranslator {
  constructor(formId, formConfig) {
    this.formId = formId;
    this.formConfig = formConfig;
    this.cachePrefix = `form_translations_${formId}`;
  }

  /**
   * Get cache key for a language
   * @param {string} language - Language code
   * @returns {string} Cache key
   */
  getCacheKey(language) {
    // Include form version/hash to invalidate cache when form changes
    const formVersion = this.getFormVersion();
    return `${this.cachePrefix}_${language}_${formVersion}`;
  }

  /**
   * Get form version hash (simple hash of form structure)
   * @returns {string} Form version hash
   */
  getFormVersion() {
    // Create a simple hash from form structure
    const formString = JSON.stringify({
      fields: this.formConfig.fields?.map(f => ({ id: f.id, title: f.title })) || [],
      pages: this.formConfig.pages?.map(p => ({ id: p.id, title: p.title })) || []
    });
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < formString.length; i++) {
      const char = formString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Collect all translatable texts from form
   * @returns {Array} Array of unique translatable texts
   */
  collectTranslatableTexts() {
    const texts = new Set();

    // Collect field titles and placeholders
    if (this.formConfig.fields && Array.isArray(this.formConfig.fields)) {
      this.formConfig.fields.forEach(field => {
        // Collect field title (used for labels, headings, and buttons)
        if (field.title && field.title.trim()) {
          const titleText = field.title.trim();
          texts.add(titleText);
          // Also add without asterisk if present
          if (titleText.endsWith(' *')) {
            texts.add(titleText.replace(/\s*\*$/, '').trim());
          }
        }
        
        // Collect placeholder text
        // Support both 'placeholder' and 'placehoder' (typo) for backward compatibility
        const placeholderText = field.placeholder || field.placehoder;
        if (placeholderText && placeholderText.trim()) {
          texts.add(placeholderText.trim());
        }
        
        // Collect content from text blocks (type 't') - extract all text from HTML
        if (field.content) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = field.content;
          
          // Remove script and style tags
          const scripts = tempDiv.querySelectorAll('script, style');
          scripts.forEach(el => el.remove());
          
          const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
          
          if (textContent) {
            // Add the full text block first (most important)
            texts.add(textContent);
            
            // Also split by common separators and add meaningful segments
            const segments = textContent.split(/(<br\s*\/?>|<p>|<\/p>|<li>|<\/li>|\r\n|\r|\n)/i);
            segments.forEach(segment => {
              const trimmed = segment.trim();
              // Only add segments that are meaningful (not just whitespace/punctuation)
              if (trimmed && trimmed.length > 2 && /[a-zA-Zא-ת]/.test(trimmed)) {
                texts.add(trimmed);
              }
            });
            
            // Also add individual sentences/phrases
            const sentences = textContent.split(/([.!?]+\s*)/);
            sentences.forEach(sentence => {
              const trimmed = sentence.trim();
              if (trimmed && trimmed.length > 3 && /[a-zA-Zא-ת]/.test(trimmed)) {
                texts.add(trimmed);
              }
            });
          }
        }
        
        // Collect select/checkbox option values
        if (field.values && Array.isArray(field.values)) {
          field.values.forEach(value => {
            if (value && typeof value === 'string' && value.trim()) {
              texts.add(value.trim());
            }
          });
        }
      });
    }

    // Collect page titles
    if (this.formConfig.pages && Array.isArray(this.formConfig.pages)) {
      this.formConfig.pages.forEach(page => {
        if (page.title && page.title.trim()) {
          texts.add(page.title.trim());
        }
      });
    }

    return Array.from(texts);
  }

  /**
   * Get cached translations
   * @param {string} language - Language code
   * @returns {Object|null} Cached translations or null
   */
  getCachedTranslations(language) {
    try {
      const cacheKey = this.getCacheKey(language);
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
    }
    return null;
  }

  /**
   * Cache translations
   * @param {string} language - Language code
   * @param {Object} translations - Translation mapping
   */
  cacheTranslations(language, translations) {
    try {
      const cacheKey = this.getCacheKey(language);
      localStorage.setItem(cacheKey, JSON.stringify(translations));
    } catch (error) {
    }
  }

  /**
   * Clear cached translations for a language
   * @param {string} language - Language code (optional, clears all if not provided)
   */
  clearCache(language = null) {
    try {
      if (language) {
        const cacheKey = this.getCacheKey(language);
        localStorage.removeItem(cacheKey);
      } else {
        // Clear all translations for this form
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith(this.cachePrefix)) {
            localStorage.removeItem(key);
          }
        });
      }
    } catch (error) {
    }
  }

  /**
   * Translate form content using AI
   * @param {string} targetLanguage - Target language code
   * @param {string} provider - AI provider (openai/claude)
   * @returns {Promise<Object>} Translation mapping
   */
  async translate(targetLanguage, provider = 'openai') {
    // Check cache first
    const cached = this.getCachedTranslations(targetLanguage);
    if (cached) {
      return {
        status: 'success',
        translations: cached,
        cached: true
      };
    }

    // Collect translatable texts
    const texts = this.collectTranslatableTexts();
    
    if (texts.length === 0) {
      return {
        status: 'error',
        message: 'No translatable text found in form'
      };
    }

    try {
      // Call translation API
      const response = await API.post('/tqtag/api/form_ai_translate', {
        form_data: this.formConfig,
        target_language: targetLanguage,
        provider: provider
      });

      if (response.status === 'success' && response.translations) {
        // Cache translations
        this.cacheTranslations(targetLanguage, response.translations);
        
        return {
          status: 'success',
          translations: response.translations,
          cached: false
        };
      } else {
        return {
          status: 'error',
          message: response.message || 'Translation failed'
        };
      }
    } catch (error) {
      return {
        status: 'error',
        message: error.message || 'Failed to translate form'
      };
    }
  }
}

